import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DateRangeControl, Delta, defaultRange, priorRange, rangePreset, sourceTypeLabel } from '@/components/shared';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { useAdminFetch } from '../../shared/useAdminFetch';
import { EconomyWindow, useEconomyHealth } from '../api/useEconomyHealth';
import { EconomyHealth } from '../api/types';
import { StatTile, Panel, formatCoins, formatPct, formatRatio } from '../components/StatTile';

const PIE_COLORS = ['#8b5cf6', '#3b82f6', '#22c55e', '#f59e0b', '#ec4899', '#14b8a6', '#eab308', '#ef4444', '#a78bfa', '#38bdf8', '#f97316', '#84cc16', '#06b6d4', '#d946ef', '#64748b', '#fb7185', '#facc15', '#4ade80'];

const TOOLTIP_STYLE = {
  backgroundColor: '#111827',
  border: '1px solid #374151',
  borderRadius: '12px',
  fontSize: '13px',
} as const;

/**
 * Economy-health dashboard (SSA-825): all 12 tiles from a single payload, with
 * the shared date window, hourly auto-refresh + manual refresh, and an "as of"
 * timestamp. Read-only; styled to the dark admin theme.
 */
export function EconomyHealthPanel({ env }: { env: Env }) {
  const [[from, to], setRange] = React.useState<[string | null, string | null]>(defaultRange);
  const preset = rangePreset(from, to);
  const win: EconomyWindow =
    preset === 'all' ? { days: 'all' } : { from: from ?? undefined, to: to ?? undefined };
  const { data, loading, error, lastUpdated, refetch } = useEconomyHealth(env, win);

  const before = priorRange(from, to);
  const prior = useAdminFetch<EconomyHealth>(
    before && {
      env,
      path: '/admin/gamification/economy-health',
      query: { from: before[0], to: before[1] },
    },
  );
  const earnSlices = (data?.earn_breakdown ?? [])
    .map((r) => ({ ...r, source: sourceTypeLabel(r.source_type) }))
    .sort((x, y) => y.coins - x.coins);
  const earnTotal = earnSlices.reduce((sum, r) => sum + r.coins, 0);
  const windowLabel =
    preset === 'all' ? 'all time' : preset !== null ? `last ${preset} days` : `${from ?? 'the start'} to ${to ?? 'today'}`;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <DateRangeControl from={from} to={to} onChange={(f, t) => setRange([f, t])} />
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              as of {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button onClick={refetch} className="text-xs text-violet-400 hover:text-violet-300">
            Refresh
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && !data && (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      )}

      {data && (
        <>
          {/* Stat row: tiles 1, 4, 8, 9 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile
              label="Outstanding liability"
              info="outstanding_liability"
              value={formatCoins(data.outstanding_liability)}
              sublabel="coins in wallets · right now"
            />
            <StatTile
              label="Mint / burn ratio"
              info="mint_burn_ratio"
              value={formatRatio(data.mint_burn_ratio)}
              delta={<Delta current={data.mint_burn_ratio} prior={prior?.mint_burn_ratio} />}
              sublabel={`${formatCoins(data.total_mint)} mint · ${formatCoins(data.total_burn)} burn`}
              tone={data.mint_burn_ratio != null && data.mint_burn_ratio > 3 ? 'warn' : 'default'}
            />
            <StatTile
              label="Visits ÷ check-ins"
              info="visits_vs_checkins"
              value={formatRatio(data.visits_vs_checkins.ratio)}
              delta={<Delta current={data.visits_vs_checkins.ratio} prior={prior?.visits_vs_checkins.ratio} />}
              sublabel={`${data.visits_vs_checkins.attributed_visits} visits · ${data.visits_vs_checkins.checkins} check-ins`}
            />
            <StatTile
              label="Avg coins / visit"
              info="avg_coins_per_visit"
              value={data.avg_coins_per_attributed_visit ?? '—'}
              delta={<Delta current={data.avg_coins_per_attributed_visit} prior={prior?.avg_coins_per_attributed_visit} />}
              sublabel="attributed visits"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Tiles 2 + 3: daily mint / burn */}
            <Panel title="Daily mint vs burn" subtitle={`Coins per day, ${windowLabel}`}>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={mergeMintBurn(data)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={{ stroke: '#374151' }} tickLine={false} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#d1d5db' }} itemStyle={{ color: '#9ca3af' }} />
                  <Legend wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }} />
                  <Line type="monotone" dataKey="Mint" stroke="#22c55e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Burn" stroke="#ef4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>

            {/* Tile 5: redemption success rate */}
            <Panel title="Redemption success rate" info="redemption_success_rate" subtitle="confirmed ÷ (confirmed + expired), per day">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.redemption_rates.map((r) => ({ day: r.day, 'Success %': r.success_rate != null ? Math.round(r.success_rate * 1000) / 10 : null }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={{ stroke: '#374151' }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#d1d5db' }} itemStyle={{ color: '#9ca3af' }} />
                  <Line type="monotone" dataKey="Success %" stroke="#8b5cf6" strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </Panel>

            {/* Tile 10: earn breakdown by source */}
            <Panel title="Earn breakdown by source" info="earn_breakdown" subtitle={`Coins minted, ${windowLabel}`}>
              {data.earn_breakdown.length === 0 ? (
                <EmptyChart />
              ) : (
                <div className="flex items-center gap-4" style={{ height: 240 }}>
                  <div className="w-2/5 h-full flex-shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={earnSlices} dataKey="coins" nameKey="source" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={1} stroke="none">
                          {earnSlices.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#d1d5db' }} itemStyle={{ color: '#9ca3af' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="flex-1 min-w-0 max-h-full overflow-y-auto pr-1 space-y-1 text-xs">
                    {earnSlices.map((r, i) => (
                      <li key={r.source_type} className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="flex-1 truncate text-gray-300" title={r.source}>{r.source}</span>
                        <span className="tabular-nums text-gray-500">{r.coins.toLocaleString()}</span>
                        <span className="w-10 text-right tabular-nums text-gray-300">
                          {earnTotal > 0 ? `${((r.coins / earnTotal) * 100).toFixed(1)}%` : '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Panel>

            {/* Tile 11: spin distribution actual vs expected */}
            <Panel title="Spin distribution vs configured weights" info="spin_distribution" subtitle={data.spin_distribution.weights_available ? `${data.spin_distribution.total_spins} spins` : 'AppConfig weights unavailable — actual only'}>
              {data.spin_distribution.buckets.length === 0 ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.spin_distribution.buckets.map((b) => ({ coins: `${b.coins}`, Actual: b.actual_pct ?? 0, Expected: b.expected_pct ?? 0 }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                    <XAxis dataKey="coins" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={{ stroke: '#374151' }} tickLine={false} />
                    <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#d1d5db' }} itemStyle={{ color: '#9ca3af' }} />
                    <Legend wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }} />
                    <Bar dataKey="Actual" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Expected" fill="#374151" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>
          </div>

          {/* Tile 7: suspicious users by reason */}
          <Panel title="Suspicious users by flag reason" info="suspicious_users" subtitle="Which heuristic is most active · current, not windowed">
            {data.suspicious_users.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">No flagged users.</p>
            ) : (
              <div className="space-y-2">
                {data.suspicious_users.map((s) => (
                  <div key={s.reason} className="flex items-center justify-between text-sm">
                    <span className="text-gray-300">{s.reason}</span>
                    <span className="text-white font-semibold tabular-nums">{s.users}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {/* Tile 6: top expiry venues */}
          <Panel title="Top venues by expiry rate" info="expiry_rate" subtitle={`expired ÷ total redemptions (refusal = expiry), ${windowLabel}`}>
            <SimpleTable
              cols={['Venue', 'Expired', 'Total', 'Expiry']}
              rows={data.top_expiry_venues.map((v) => [
                v.venue_name ?? v.venue_id.slice(0, 8),
                String(v.expired),
                String(v.total),
                formatPct(v.expiry_rate != null ? v.expiry_rate * 100 : null),
              ])}
              empty="No redemptions in window."
            />
          </Panel>

          {/* Tile 12: top markets by activity */}
          <Panel title="Top markets by activity" info="top_markets" subtitle="venues × attributed visits · all time, not windowed">
            <SimpleTable
              cols={['Market', 'State', 'Venues', 'Visits']}
              rows={data.top_markets.map((m) => [
                m.name ?? m.market_id.slice(0, 8),
                m.state ?? '—',
                String(m.venue_count),
                String(m.visit_count),
              ])}
              empty="No market activity."
            />
          </Panel>
        </>
      )}
    </div>
  );
}

function mergeMintBurn(data: { daily_mint: { day: string; coins: number }[]; daily_burn: { day: string; coins: number }[] }) {
  const byDay = new Map<string, { day: string; Mint: number; Burn: number }>();
  for (const m of data.daily_mint) byDay.set(m.day, { day: m.day, Mint: m.coins, Burn: 0 });
  for (const b of data.daily_burn) {
    const row = byDay.get(b.day) ?? { day: b.day, Mint: 0, Burn: 0 };
    row.Burn = b.coins;
    byDay.set(b.day, row);
  }
  return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
}

function EmptyChart() {
  return <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">No data in window.</div>;
}

function SimpleTable({ cols, rows, empty }: { cols: string[]; rows: string[][]; empty: string }) {
  if (rows.length === 0) return <p className="text-sm text-gray-500 py-4">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
            {cols.map((c) => (
              <th key={c} className="py-2 pr-4 font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-800/50">
              {r.map((cell, j) => (
                <td key={j} className={`py-2 pr-4 ${j === 0 ? 'text-gray-200' : 'text-gray-400 tabular-nums'}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
